import { clone, isString, isArray } from '../utils'
import { AggregatingValidator, messages, validationError } from '../errors'

export type Direction = 'asc' | 'desc'
export interface OrderBySpec {
  [field: string]: Direction
}
export type OrderBy = OrderBySpec[]

interface WithConstructor {
  // eslint-disable-next-line @typescript-eslint/prefer-function-type
  new (...args: any): any
}

const sortMixin = (Base: WithConstructor = class {}) =>
  class extends Base {
    orderBy: OrderBy
    invalidArguments: string[]

    constructor(obj: any = {}) {
      super(obj)
      this.orderBy = obj.orderBy || []
      this.invalidArguments = obj.invalidArguments || []
    }

    ascending(...fields: any[]) {
      return this._sortClause('asc', '.ascending', fields)
    }

    descending(...fields: any[]) {
      return this._sortClause('desc', '.descending', fields)
    }

    getSortModel() {
      if (this.invalidArguments.length > 0) {
        throw validationError(
          messages.sortBuilderInvalid(this.invalidArguments)
        )
      }
      return this.orderBy
    }

    setSortModel(sortModel: any[]) {
      return this._copySort(sortModel, [])
    }

    _sortClause(operatorSymbol: Direction, operatorName: string, args: any[]) {
      let orderBy: OrderBy = []
      if (this.orderBy) {
        orderBy = clone(this.orderBy)
      }

      const appendClause = createAppendClauseFn(orderBy, operatorSymbol)
      const effectiveArgs =
        isArray(args[0]) && args.length === 1
          ? args[0]
          : Array.prototype.slice.call(args, 0)
      const [newInvalidArguments, valid] = this._sortValidator(operatorName)
        .arityIsAtLeastOne(args)
        .allStrings(effectiveArgs)
        .validateAndAggregate()

      if (valid) {
        effectiveArgs.forEach(appendClause)
      }

      return this._copySort(orderBy, newInvalidArguments)
    }

    _sortValidator(operatorName: string) {
      return new SortValidator(operatorName, this.invalidArguments)
    }

    _copySort(orderBy: OrderBy, invalidArguments: string[]) {
      return new (this.constructor as WithConstructor)({
        ...this,
        orderBy,
        invalidArguments,
      })
    }
  }

function createAppendClauseFn(destination: OrderBy, direction: Direction) {
  return (field: string) => {
    const spec: OrderBySpec = { [field]: direction }
    destination.push(spec)
  }
}

export class SortValidator extends AggregatingValidator {
  constructor(public operatorName: string, previousInvalidArguments: string[]) {
    super(previousInvalidArguments)
  }

  allStrings(effectiveArgs: any[]) {
    return this.addValidation(
      () => effectiveArgs.every(isString),
      () => {
        return messages.sortValidations.typeIsStringOrArrayOfStrings(
          this.operatorName,
          effectiveArgs
        )
      }
    )
  }
}

export class Sort extends sortMixin() {}

export default sortMixin
